import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { playSound, speak } from '../utils/sounds';
import { getApiUrl, getExerciseEndpoint } from '../config/api';
import { getSuccessMessage } from '../utils/encouragements';

const PoesieExercise = () => {
  const { level } = useParams();
  const navigate = useNavigate();
  const [exercise, setExercise] = useState(null);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [isReading, setIsReading] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const poemContainerRef = React.useRef(null);
  const hasSpokenInstructionsRef = React.useRef(false);

  const loadExercise = useCallback(async () => {
    try {
      const endpoint = getExerciseEndpoint('poesie', level);
      const url = getApiUrl(endpoint);
      console.log('Loading poetry from:', url);
      const response = await axios.get(url);
      console.log('Poetry loaded:', response.data);
      setExercise(response.data);
      setCurrentLineIndex(0);
      setHasStarted(false);
      hasSpokenInstructionsRef.current = false; // Réinitialiser pour le nouvel exercice
      setTimeout(() => {
        if (!hasSpokenInstructionsRef.current) {
          speak(response.data.instructions);
          setTimeout(() => {
            speak(response.data.poem.title);
          }, 2000);
          hasSpokenInstructionsRef.current = true;
        }
      }, 500);
    } catch (error) {
      console.error('Error loading poetry:', error);
      setExercise({ poem: { title: 'ERROR', lines: [] }, instructions: 'Erreur de chargement.' });
    }
  }, [level]);

  useEffect(() => {
    loadExercise();
  }, [loadExercise]);

  const startPoem = () => {
    setHasStarted(true);
    // Ne pas appeler readCurrentLine() ici, le useEffect le fera
  };

  const readCurrentLine = () => {
    if (!exercise || !exercise.poem) return;

    const currentLine = exercise.poem.lines[currentLineIndex];
    if (currentLine) {
      setIsReading(true);
      speak(currentLine);
      setTimeout(() => {
        setIsReading(false);
      }, currentLine.length * 100 + 1000); // Temps approximatif de lecture
    }
  };

  const handleRepeat = () => {
    playSound('click');
    // L'enfant a "répété" (on ne vérifie pas), passer à la ligne suivante
    if (currentLineIndex < exercise.poem.lines.length - 1) {
      setCurrentLineIndex(currentLineIndex + 1);
    } else {
      // Fin de la poésie
      playSound('success');
      const childName = localStorage.getItem('childName');
      const successMessage = getSuccessMessage(childName);
      speak(successMessage + ' Tu as terminé la poésie !');

      // Lire l'auteur à la fin
      if (exercise.poem.author) {
        setTimeout(() => {
          speak(`Cette poésie a été écrite par ${exercise.poem.author}`);
        }, 2500);
      }

      setTimeout(() => {
        navigate('/subjects');
      }, exercise.poem.author ? 6000 : 3000);
    }
  };

  const handleReplay = () => {
    playSound('click');
    readCurrentLine();
  };

  const handleLineClick = (lineIndex) => {
    // Permettre de cliquer sur la ligne actuelle ou les lignes précédentes
    if (!hasStarted || lineIndex > currentLineIndex) return;

    playSound('click');

    // Si on clique sur une ligne précédente, repositionner la lecture à cette ligne
    if (lineIndex < currentLineIndex) {
      setCurrentLineIndex(lineIndex);
    } else {
      // Si c'est la ligne actuelle, juste la relire sans changer l'index
      const line = exercise.poem.lines[lineIndex];
      setIsReading(true);
      speak(line);
      setTimeout(() => {
        setIsReading(false);
      }, line.length * 100 + 1000);
    }
  };

  useEffect(() => {
    if (hasStarted && currentLineIndex >= 0 && exercise) {
      readCurrentLine();

      // Scroll pour centrer la ligne actuelle
      if (poemContainerRef.current) {
        const lineElement = poemContainerRef.current.children[currentLineIndex];
        if (lineElement) {
          lineElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLineIndex, hasStarted]);

  if (!exercise) {
    return <div className="loading">Chargement...</div>;
  }

  return (
    <div>
      <div className="header">
        <h1>🎭 Poésie - Niveau {level}</h1>
        <h2 style={{ marginTop: '20px', color: '#9B51E0' }}>{exercise.poem.title}</h2>
        {exercise.poem.author && (
          <p style={{ fontStyle: 'italic', color: '#666' }}>par {exercise.poem.author}</p>
        )}
      </div>

      <div className="card" style={{ position: 'relative', paddingBottom: hasStarted ? '120px' : '20px' }}>
        {/* Illustration d'automne */}
        <div style={{
          textAlign: 'center',
          fontSize: '6rem',
          marginBottom: '30px',
          opacity: 0.3,
          position: 'absolute',
          top: '20px',
          right: '20px',
          zIndex: 0
        }}>
          🍂🌧️🍁
        </div>

        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Affichage de toutes les lignes avec highlight de la ligne actuelle */}
          <div
            ref={poemContainerRef}
            style={{
              textAlign: 'left',
              fontSize: '1.5rem',
              lineHeight: '2.5',
              marginBottom: hasStarted ? '100px' : '40px',
              maxWidth: '600px',
              margin: '0 auto',
              padding: '20px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '10px',
              maxHeight: '400px',
              overflowY: 'auto'
            }}
          >
            {exercise.poem.lines.map((line, index) => {
              const isClickable = hasStarted && index <= currentLineIndex;
              const isCurrent = index === currentLineIndex && hasStarted;
              const isPrevious = index < currentLineIndex && hasStarted;

              return (
                <div
                  key={index}
                  onClick={() => handleLineClick(index)}
                  style={{
                    padding: '8px',
                    borderRadius: '5px',
                    backgroundColor: isCurrent ? '#FFE5B4' : 'transparent',
                    fontWeight: isCurrent ? 'bold' : 'normal',
                    color: isPrevious ? '#888' : '#333',
                    transition: 'all 0.3s ease',
                    cursor: isClickable ? 'pointer' : 'default',
                    userSelect: 'none',
                    opacity: isPrevious ? 0.7 : 1
                  }}
                  title={isClickable ? 'Clique pour réécouter cette ligne' : ''}
                  onMouseEnter={(e) => {
                    if (isClickable && !isCurrent) {
                      e.currentTarget.style.backgroundColor = '#f0f0f0';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isCurrent) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {isCurrent && '👉 '}{line}
                </div>
              );
            })}
          </div>

          {!hasStarted ? (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button
                className="btn btn-primary bounce"
                onClick={startPoem}
                style={{
                  fontSize: '2rem',
                  padding: '30px 60px'
                }}
              >
                🎭 Commencer la poésie
              </button>
            </div>
          ) : (
            <>
              <div style={{
                textAlign: 'center',
                marginBottom: '20px',
                fontSize: '1.2rem',
                color: '#666'
              }}>
                Ligne {currentLineIndex + 1} sur {exercise.poem.lines.length}
              </div>

              {/* Boutons fixés en bas */}
              <div style={{
                position: 'absolute',
                bottom: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: '20px',
                justifyContent: 'center',
                flexWrap: 'wrap',
                width: '100%',
                maxWidth: '800px',
                padding: '0 20px'
              }}>
                <button
                  className="btn btn-secondary"
                  onClick={handleReplay}
                  disabled={isReading}
                  style={{
                    fontSize: '1.5rem',
                    padding: '20px 40px',
                    opacity: isReading ? 0.6 : 1
                  }}
                >
                  🔊 Réécouter
                </button>
                <button
                  className="btn btn-success celebrate"
                  onClick={handleRepeat}
                  disabled={isReading}
                  style={{
                    fontSize: '2rem',
                    padding: '30px 60px',
                    opacity: isReading ? 0.6 : 1
                  }}
                >
                  ✅ J'ai répété
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: '40px', display: 'flex', gap: '20px', justifyContent: 'center' }}>
        <button
          className="btn btn-warning"
          onClick={() => navigate('/subjects')}
        >
          🏠 Retour
        </button>
        {hasStarted && (
          <button
            className="btn btn-primary"
            onClick={loadExercise}
          >
            🔄 Recommencer
          </button>
        )}
      </div>
    </div>
  );
};

export default PoesieExercise;
